// E:\new-react-project\the-ultimate-guide\src\core-pages\PickUsername.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../Security/firebase-config";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { createUserRecord } from "../utils/createUserRecord";

import "./PickUsername.css";

export default function PickUsername() {
  const [username, setUsername] = useState("");
  const [currentUsername, setCurrentUsername] = useState(null);
  const [hasUidsDoc, setHasUidsDoc] = useState(false);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { search } = useLocation();
  const next = useMemo(
    () => new URLSearchParams(search).get("next") || "/home",
    [search]
  );

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────
  const validateClientSide = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return "Please enter a username.";

    // Keep this aligned with your CF if you want the same UX.
    // Your Cloud Function enforces: 3–20, letters/numbers/underscore only.
    const ok = /^[a-zA-Z0-9_]{3,20}$/.test(trimmed);
    if (!ok) {
      return "Username must be 3–20 characters and contain only letters, numbers, and underscores.";
    }
    return null;
  };

  const randomUsername = () => {
    const digits = String(Math.floor(Math.random() * 10000)).padStart(4, "0");
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const a = letters[Math.floor(Math.random() * letters.length)];
    const b = letters[Math.floor(Math.random() * letters.length)];
    return `user${digits}${a}${b}`;
  };

  // Claim a username using your immutable /usernames + /uids rules
  // (only works if /uids doesn't exist yet)
  const claimUsernameFirstTime = async (user, desiredUsername) => {
    const uid = user.uid;
    const trimmed = desiredUsername.trim();
    const unameLower = trimmed.toLowerCase();

    // 0) block duplicates BEFORE any writes
    const unameRef = doc(db, "usernames", unameLower);
    const unameSnap = await getDoc(unameRef);
    if (unameSnap.exists()) {
      throw Object.assign(new Error("USERNAME_TAKEN"), { code: "username-taken" });
    }

    // 1) users/{uid}
    // If user doc doesn't exist, create it (create rule allows more fields).
    // If it exists, only patch username (update rule allows username).
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await createUserRecord(user, { username: trimmed });
    } else {
      await setDoc(userRef, { username: trimmed }, { merge: true });
    }

    // 2) usernames/{unameLower} (immutable create)
    await setDoc(doc(db, "usernames", unameLower), {
      uid,
      username: trimmed,
      createdAt: serverTimestamp(),
    });

    // 3) uids/{uid} (immutable create)
    await setDoc(doc(db, "uids", uid), {
      uid,
      username: trimmed,
      unameLower,
      createdAt: serverTimestamp(),
    });

    return { username: trimmed, unameLower };
  };

  const changeUsernameViaCloudFunction = async (newUsername) => {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "changeUsername");
    const res = await fn({ newUsername });
    return res?.data;
  };

  // ─────────────────────────────────────────────────────────────
  // Load current username + whether /uids exists
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const user = auth.currentUser;
      const uid = user?.uid;
      if (!uid) return;

      try {
        const [userSnap, uidsSnap] = await Promise.all([
          getDoc(doc(db, "users", uid)),
          getDoc(doc(db, "uids", uid)),
        ]);

        if (cancelled) return;

        setHasUidsDoc(uidsSnap.exists());

        // Prefer /uids (it’s your “directory” of usernames)
        if (uidsSnap.exists()) {
          const u = uidsSnap.data() || {};
          setCurrentUsername(u.username ?? null);
          // pre-fill input with current username for convenience
          if (typeof u.username === "string" && u.username) {
            setUsername(u.username);
          }
          return;
        }

        if (userSnap.exists()) {
          const d = userSnap.data() || {};
          setCurrentUsername(d.username ?? null);
          if (typeof d.username === "string" && d.username) {
            setUsername(d.username);
          }
        } else {
          // No docs yet (rare). Still fine.
          setCurrentUsername(null);
        }
      } catch (e) {
        console.error("PickUsername load failed:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────
  const handleKeepCurrent = () => {
    navigate(next, { replace: true });
  };

  const handleUseRandom = async () => {
    setError("");
    const user = auth.currentUser;
    const uid = user?.uid;

    if (!uid) {
      setError("You must be logged in to continue.");
      return;
    }

    // If they already have a username, "random" just means "keep it"
    if (currentUsername) {
      navigate(next, { replace: true });
      return;
    }

    setLoading(true);
    try {
      // If /uids already exists, we cannot create new indices.
      // In that case, generate a random and change via CF.
      if (hasUidsDoc) {
        for (let i = 0; i < 20; i++) {
          const candidate = randomUsername();
          try {
            await changeUsernameViaCloudFunction(candidate);
            navigate(next, { replace: true });
            return;
          } catch (e) {
            const msg = (e?.message || "").toString();
            const code = (e?.code || "").toString();
            if (code === "functions/already-exists" || msg.includes("USERNAME_TAKEN")) {
              continue; // try again
            }
            throw e;
          }
        }
        throw new Error("Could not find an available random username. Please try again.");
      }

      // First-time claim path (no /uids)
      for (let i = 0; i < 20; i++) {
        const candidate = randomUsername();
        try {
          await claimUsernameFirstTime(user, candidate);
          navigate(next, { replace: true });
          return;
        } catch (e) {
          if (e?.code === "username-taken" || e?.message === "USERNAME_TAKEN") {
            continue; // try another candidate
          }
          throw e;
        }
      }

      throw new Error("Could not find an available random username. Please try again.");
    } catch (e) {
      console.error("Use random username failed:", e);
      setError("Failed to assign a random username. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const user = auth.currentUser;
    const uid = user?.uid;

    if (!uid) {
      setError("You must be logged in to pick a username.");
      return;
    }

    const trimmed = username.trim();
    const validationError = validateClientSide(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      // If they've already claimed a username before, /uids exists and
      // your rules prevent recreating /uids or deleting old /usernames.
      // So we MUST use the Cloud Function transaction.
      if (hasUidsDoc) {
        await changeUsernameViaCloudFunction(trimmed);
        navigate(next, { replace: true });
        return;
      }

      // Otherwise, first-time claim path (immutable creates)
      await claimUsernameFirstTime(user, trimmed);
      navigate(next, { replace: true });
    } catch (err) {
      console.error("PickUsername submit failed:", err);

      const code = (err?.code || "").toString();
      const message = (err?.message || "").toString();

      // Cloud Function "already exists" or sentinel
      if (code === "functions/already-exists" || message.includes("USERNAME_TAKEN") || err?.code === "username-taken") {
        setError("That username is already taken. Please try another.");
      } else if (code === "functions/failed-precondition") {
        // Your CF uses failed-precondition for cooldown
        setError(message || "You can’t change your username yet.");
      } else if (code === "permission-denied") {
        setError("Missing permissions to save username. (Check Firestore rules / flow.)");
      } else {
        setError("Failed to save username. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="Pick-Usr">
      <div className="Pick-Usr__card">
        <h2 className="Pick-Usr__title">Choose a username</h2>
        <p className="Pick-Usr__subtitle">
          Pick a unique username for your account.
        </p>

        {currentUsername && (
          <div className="Pick-Usr__panel">
            <p className="Pick-Usr__panelText">
              Your account already has a username: <b className="Pick-Usr__Username">{currentUsername}</b>
            </p>
            <button
              type="button"
              className="Pick-Usr__btn"
              onClick={handleKeepCurrent}
              disabled={loading}
            >
              Keep this username and continue
            </button>
          </div>
        )}

        {!currentUsername && (
          <div className="Pick-Usr__panel">
            <button
              type="button"
              className="Pick-Usr__btn"
              onClick={handleUseRandom}
              disabled={loading}
            >
              {loading ? "Working..." : "Use a random username and continue"}
            </button>
          </div>
        )}

        <form className="Pick-Usr__form" onSubmit={handleSubmit}>
          <label className="Pick-Usr__label">
            Enter a username{currentUsername ? " (or change it)" : ""}:
          </label>

          <input
            className="Pick-Usr__input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            autoComplete="off"
            disabled={loading}
          />

          <div className="Pick-Usr__btnRow">
            <button
              type="submit"
              className="Pick-Usr__btn Pick-Usr__btn--primary"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : currentUsername
                ? "Change username"
                : "Save username"}
            </button>
          </div>

          {error && <p className="Pick-Usr__error">{error}</p>}
        </form>
      </div>
    </div>
  );
}